import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import { ThemeProvider } from '@mui/material/styles';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { TablePaginationActions, theme } from '../../../../utils/TableStyles';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { CustomAutocomplete, CustomTextField } from '../../../../utils/StyledInputs';
import { EntitiesDialog } from '../../../../utils/EntitiesDialog';
import API from '../../../../api';
import { usePopup } from '../../../../context/PopupContext';
import { useUser } from '../context/UserContext';

export default function AuthorizationsTab({
  value,
  userID,
  canManageUserRoles,
}) {
  const { showPopup, setPopUpText } = usePopup();
  const { isLoading, errorMessage, userInfo } = useUser();

  const is600Screen = useMediaQuery('(min-width: 600px)');

  const [authApiCalled, setAuthApiCalled] = useState(false);
  const [openNewAuthorization, setOpenNewAuthorization] = useState(false);
  const [openShowAuthorization, setOpenShowAuthorization] = useState(false);
  const [openDeleteAuthorization, setOpenDeleteAuthorization] = useState(false);
  const [authorizationList, setAuthorizationList] = useState([]);
  const [authorizationToShow, setAuthorizationToShow] = useState({});
  const [openNewAuthEntitiesFilter, setOpenNewAuthEntitiesFilter] = useState(false);
  const [newAuthEntity, setNewAuthEntity] = useState(null);
  const [newAuthObject, setNewAuthObject] = useState({});
  const [newAuthType, setNewAuthType] = useState('');
  const [authorizationToDelete, setAuthorizationToDelete] = useState({});
  const [authError, setAuthError] = useState('');

  const [authTypeListCalled, setAuthTypeListCalled] = useState(false);
  const [authTypeList, setAuthTypeList] = useState([]);
  const [authObjListCalled, setAuthObjListCalled] = useState(false);
  const [authObjList, setAuthObjList] = useState([]);
  const [authDataCalled, setAuthDataCalled] = useState(false);

  // authorization table pagination
  const [authPage, setAuthPage] = useState(0);
  const [authRowsPerPage, setAuthRowsPerPage] = useState(is600Screen ? 10 : 5);

  const emptyAuthRows = authPage > 0 ? Math.max(0, (1 + authPage) * authRowsPerPage - authorizationList.length) : 0;

  // change auth page
  const handleAuthPage = (event, newPage) => {
    setAuthPage(newPage);
  };

  // change auth rows per page
  const handleAuthRowsPerPage = (event) => {
    setAuthRowsPerPage(parseInt(event.target.value, 10));
    setAuthPage(0);
  };

  // Call Authorization Api
  useEffect(() => {
    if (value === 5 && !authApiCalled) {
      getAuthorizationList();
    }
  }, [value, authApiCalled]);

  // Get authorizationList
  const getAuthorizationList = () => {
    API.get(`/userauth/user/${userID}`)
      .then((response) => {
        setAuthApiCalled(true);
        setAuthorizationList(response.data);
      })
      .catch((error) => {
        setAuthApiCalled(true);

        if (error?.response?.status === 403) {
          setAuthError(error.response.data?.message || 'Utente non autorizzato');
        } else {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        }
      });
  };

  /* --------------------------- Show Authorization --------------------------- */
  const handleOpenShowAuthorizationDialog = (auth) => {
    setOpenShowAuthorization(true);
    setAuthorizationToShow(auth);
  };

  const handleCloseShowAuthorizationDialog = () => {
    setOpenShowAuthorization(false);
    setAuthorizationToShow({});
  };

  /* -------------------------- Delete Authorization -------------------------- */
  const handleOpenDeleteAuthorizationDialog = (auth) => {
    setOpenDeleteAuthorization(true);
    setAuthorizationToDelete(auth);
  };

  const handleCloseDeleteAuthorizationDialog = () => {
    setOpenDeleteAuthorization(false);
    setAuthorizationToDelete({});
  };

  const handleDeleteAuthorization = () => {
    API.delete(`/userauth/${authorizationToDelete.id}`)
      .then((response) => {
        handleCloseDeleteAuthorizationDialog();
        getAuthorizationList();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ---------------------------- New Authorization --------------------------- */
  const handleOpenNewAuthorizationDialog = () => {
    setOpenNewAuthorization(true);
    getNewAuthorizationData();
  };

  // Get authTypeList & authObjectList
  const getNewAuthorizationData = () => {
    if (!authTypeListCalled) {
      API.get(`/authtypelist`).then((response) => {
        setAuthTypeList(response.data);
        setAuthTypeListCalled(true);
      });
    }

    if (!authObjListCalled) {
      API.get(`/authobjectlist`).then((response) => {
        setAuthObjList(response.data);
        setAuthObjListCalled(true);
      });
    }
  };

  // Set all auth apis called
  useEffect(() => {
    if (authTypeListCalled && authObjListCalled) {
      setAuthDataCalled(true);
    }
  }, [authTypeListCalled, authObjListCalled]);

  // Close new auth dialog
  const handleCloseNewAuthorizationDialog = () => {
    setOpenNewAuthorization(false);

    setNewAuthEntity(null);
    setNewAuthObject({});
    setNewAuthType('');
  };

  // Close new auth entity dialog
  const handleCloseNewAuthEntitiesFilterDialog = () => {
    setOpenNewAuthEntitiesFilter(false);
  };

  // Get new auth Entity
  const getNewAuthEntity = (selectedEntity) => {
    setNewAuthEntity(selectedEntity);
  };

  const handleCheckboxChange = (event, authType) => {
    const isChecked = event.target.checked;
    const type = authType.type;

    setNewAuthType((prev) => {
      if (isChecked) {
        return prev.includes(type) ? prev : prev + type;
      }
      return prev.replace(type, '');
    });
  };

  const getAuthTypeCheckboxLabel = (type) => {
    switch (type) {
      case 'R':
        return 'Lettura';
      case 'W':
        return 'Scrittura';
      case 'D':
        return 'Cancellazione';
      default:
        return '';
    }
  };

  const onAddAuthorization = (e) => {
    e.preventDefault();

    const data = {
      user_id: userInfo.id,
      entity_id: newAuthEntity ? newAuthEntity.id : '',
      object_id: newAuthObject ? newAuthObject.id : '',
      authorization_type: newAuthType,
    };

    let itemsLeft = false;
    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (
          value === undefined ||
          value === null ||
          value === 'Invalid Date' ||
          (typeof value === 'string' && value.length === 0)
        ) {
          itemsLeft = true;
          showPopup('warning', 'Inserisci i dati mancanti');
        }
      }
    });

    if (!itemsLeft) {
      API.post(`/userauth`, data)
        .then((response) => {
          getAuthorizationList();
          handleCloseNewAuthorizationDialog();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  return (
    <>
      {isLoading || !authApiCalled ? (
        LoadingUI()
      ) : authError.length > 0 ? (
        ErrorUI(authError)
      ) : errorMessage.length === 0 ? (
        <>
          <Stack direction="row" justifyContent="end" gap="10px">
            <DefaultContainedButton
              disabled={!canManageUserRoles}
              startIcon={<AddIcon />}
              onClick={handleOpenNewAuthorizationDialog}
            >
              Aggiungi Autorizzazione
            </DefaultContainedButton>

            {/* new authorization dialog */}
            <Dialog fullWidth open={openNewAuthorization} onClose={() => {}}>
              <DialogTitle sx={{ textAlign: 'center' }}>Aggiungi Autorizzazione</DialogTitle>
              <form onSubmit={onAddAuthorization}>
                <DialogContent>
                  {authDataCalled ? (
                    <Grid container spacing={3}>
                      {/* Entità */}
                      <Grid item xs={12}>
                        {!newAuthEntity ? (
                          <DefaultOutlinedButton
                            customStyles={{ width: '100%' }}
                            onClick={() => setOpenNewAuthEntitiesFilter(!openNewAuthEntitiesFilter)}
                          >
                            Cerca Entità
                          </DefaultOutlinedButton>
                        ) : (
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <CustomTextField
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                      <Tooltip title="Modifica" placement="left" arrow>
                                        <EditIcon
                                          onClick={() => setOpenNewAuthEntitiesFilter(!openNewAuthEntitiesFilter)}
                                        />
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                                value={newAuthEntity.name}
                                label="Entità"
                              />
                            </Grid>
                          </Grid>
                        )}

                        <EntitiesDialog
                          openDialog={openNewAuthEntitiesFilter}
                          closeDialog={handleCloseNewAuthEntitiesFilterDialog}
                          outputEntity={getNewAuthEntity}
                          context={'userAuth'}
                        />
                      </Grid>

                      {/* Oggetto */}
                      <Grid item xs={12} sx={{ overflow: 'auto' }}>
                        <CustomAutocomplete
                          required
                          id="auth-object"
                          options={authObjList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setNewAuthObject(value)}
                          renderInput={(params) => <TextField {...params} label="Oggetto *" variant="outlined" />}
                        />
                      </Grid>

                      {/* Tipo di autorizzazione */}
                      <Grid item xs={12} sx={{ overflow: 'auto' }}>
                        <FormGroup
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                            alignItems: 'center',
                          }}
                        >
                          {authTypeList.map((authType) => (
                            <FormControlLabel
                              key={authType.type}
                              control={
                                <Checkbox
                                  checked={newAuthType.includes(authType.type)}
                                  onChange={(event) => handleCheckboxChange(event, authType)}
                                />
                              }
                              label={getAuthTypeCheckboxLabel(authType.type)}
                            />
                          ))}
                        </FormGroup>
                      </Grid>
                    </Grid>
                  ) : (
                    LoadingUI()
                  )}
                </DialogContent>
                <DialogActions>
                  <DefaultOutlinedButton onClick={handleCloseNewAuthorizationDialog}>Chiudi</DefaultOutlinedButton>
                  <DefaultContainedButton type="submit">Aggiungi</DefaultContainedButton>

                </DialogActions>
              </form>
            </Dialog>
          </Stack>

          {/* show authorization dialog */}
          <Dialog fullWidth open={openShowAuthorization} onClose={() => {}}>
            <DialogTitle>Visualizzazione Autorizzazione</DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ marginTop: '1px' }}>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Entità"
                    inputProps={{ readOnly: true }}
                    value={authorizationToShow.entity ? authorizationToShow.entity.name : ''}
                  />
                </Grid>

                <Grid item xs={6}>
                  <CustomTextField
                    label="Oggetto Autorizzazione"
                    inputProps={{ readOnly: true }}
                    value={
                      authorizationToShow.authorization_object ? authorizationToShow.authorization_object.name : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <CustomTextField
                    label="Tipo Autorizzazione"
                    inputProps={{ readOnly: true }}
                    value={
                      authorizationToShow.authorization_type ? authorizationToShow.authorization_type.description : ''
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <CustomTextField
                    label="Data Creazione"
                    inputProps={{ readOnly: true }}
                    value={
                      authorizationToShow.created_at ? dayjs(authorizationToShow.created_at).format('DD/MM/YYYY') : ''
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <DefaultOutlinedButton onClick={handleCloseShowAuthorizationDialog}>Chiudi</DefaultOutlinedButton>
            </DialogActions>
          </Dialog>

          {/* delete authorization dialog */}
          <Dialog fullWidth open={openDeleteAuthorization} onClose={() => {}}>
            <DialogTitle>Eliminazione Autorizzazione</DialogTitle>
            <DialogContent>Sei sicuro di voler eliminare questa autorizzazione?</DialogContent>
            <DialogActions>
              <DefaultOutlinedButton onClick={handleCloseDeleteAuthorizationDialog}>Chiudi</DefaultOutlinedButton>
              <DefaultContainedButton onClick={() => handleDeleteAuthorization()}>Elimina</DefaultContainedButton>

            </DialogActions>
          </Dialog>

          <Grid container spacing={3} sx={{ marginTop: '24px' }}>
            <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
              <Card>
                {/* Authorization table */}
                <TableContainer sx={{ padding: '24px 24px 6px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Entità</TableCell>
                        <TableCell>Oggetto Autorizzazione</TableCell>
                        <TableCell>Tipo Autorizzazione</TableCell>
                        <TableCell>Data Creazione</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    {authorizationList.length > 0 ? (
                      <TableBody>
                        {authorizationList
                          .slice(authPage * authRowsPerPage, authPage * authRowsPerPage + authRowsPerPage)
                          .map((auth) => (
                            <TableRow hover key={auth.id}>
                              <TableCell>{auth.entity.name}</TableCell>
                              <TableCell>{auth.authorization_object.name}</TableCell>
                              <TableCell>{auth.authorization_type.description}</TableCell>
                              <TableCell>{dayjs(auth.created_at).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Tooltip title="Visualizza dettaglio" arrow>
                                    <Box>
                                      <CustomIconButton onClick={() => handleOpenShowAuthorizationDialog(auth)}>
                                        <DescriptionOutlinedIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>

                                  <Tooltip title="Elimina" arrow>
                                    <Box>
                                      <CustomIconButton
                                        disabled={!canManageUserRoles}
                                        onClick={() => handleOpenDeleteAuthorizationDialog(auth)}
                                      >
                                        <DeleteIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}

                        {emptyAuthRows > 0 && (
                          <TableRow style={{ height: 55 * emptyAuthRows }}>
                            <TableCell colSpan={6} />
                          </TableRow>
                        )}
                      </TableBody>
                    ) : (
                      // No Authorization View
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2">Nessuna autorizzazione disponibile</Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                    <TableFooter>
                      <TableRow>
                        <ThemeProvider theme={theme}>
                          <TablePagination
                            sx={{ borderBottom: 'none' }}
                            rowsPerPageOptions={[]}
                            colSpan={6}
                            count={authorizationList.length}
                            rowsPerPage={authRowsPerPage}
                            page={authPage}
                            onPageChange={handleAuthPage}
                            onRowsPerPageChange={handleAuthRowsPerPage}
                            labelRowsPerPage="Righe per Pagina"
                            ActionsComponent={TablePaginationActions}
                          />
                        </ThemeProvider>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        // Error handler
        ErrorUI(errorMessage)
      )}
    </>
  );
}
